import { Platform } from 'react-native';
import {
  ColumnModel,
  FilterModel,
  metaPillCount,
  metaPillToFilter,
} from '../../filter/domain';

export const AssetFilterKey = 'ASSET_FILTER';
export const AssetListOrderKey = 'ASSET_LIST_ORDER_KEY';

export const AssetFilterBaseColumns = [
  'description',
  'productName',
  'lastUpdated',
  'lastScannedAt',
  'reports',
  'routines',
  'addons',
  'notes',
  'alerts',
  'lastAddress',
  'tagCode',
  'groups',
  'groupNames',
];

export const GlobalAdminColumns = ['createdAt', 'createdBy', 'workspaces'];

export class AssetFilterModel implements FilterModel, ColumnModel {
  keyword?: string;
  columns?: string[];
  groups?: string[];
  isCombinedGroupsFilter?: boolean;
  includeAllGroups?: boolean;
  includeUngrouped?: boolean;

  constructor(data?: any) {
    this.keyword = data?.keyword;
    this.columns =
      data?.columns ||
      (Platform.OS === 'web'
        ? [
            'description',
            'lastUpdated',
            'reports',
            'lastAddress',
            'groups',
            'groupNames',
          ]
        : ['reports', 'lastAddress']);
    this.groups = data?.groups;
    this.isCombinedGroupsFilter = data?.isCombinedGroupsFilter;

    // Migrate old format: if new pill fields are absent, infer from the legacy array.
    if (
      data?.includeAllGroups !== undefined ||
      data?.includeUngrouped !== undefined
    ) {
      this.includeAllGroups = data.includeAllGroups ?? true;
      this.includeUngrouped = data.includeUngrouped ?? true;
    } else {
      const hasSpecificGroups = data?.groups && data.groups.length > 0;
      this.includeAllGroups = !hasSpecificGroups;
      this.includeUngrouped = !hasSpecificGroups;
    }
  }

  public getFilter(): any {
    const filter = {} as any;

    if (this.keyword) {
      filter.or = [
        { name: { contains: this.keyword } },
        { description: { contains: this.keyword } },
      ];
    }

    return filter;
  }

  /**
   * Maps the model to the `ListAssetsFilter` shape sent to the `listAssets`
   * resolver. This is the single source of truth for the server filter — both
   * the asset list and the filter-panel count query consume it, so they can't
   * diverge. Crucially it NEVER emits `includeAllGroups` (a client-only UI
   * marker that is not part of the GraphQL schema); the All/Not-Grouped pill
   * state is translated to `includeUngrouped` + `groups` exactly the way
   * `ActivityFilterModel.getFilter()` does.
   */
  public toListFilter(): any {
    const filter: any = {
      columns:
        this.columns && this.columns.length > 0 ? this.columns : ['name'],
    };

    if (this.keyword) {
      filter.keyword = this.keyword;
    }

    // Group pill state → API fields (shared metaPillToFilter, same as
    // ActivityFilterModel). Default sends no group fields at all, preserving the
    // legacy "show everything" behavior. `groups` here is the field name the
    // listAssets resolver expects (vs activity's `groupIds`).
    const groups = metaPillToFilter({
      includeAll: this.includeAllGroups ?? true,
      includeNone: this.includeUngrouped ?? true,
      items: (this.groups ?? []).filter((g): g is string => !!g),
    });
    if (groups.ids) {
      filter.groups = groups.ids;
      if (this.isCombinedGroupsFilter) {
        filter.isCombinedGroupsFilter = this.isCombinedGroupsFilter;
      }
    }
    if (groups.includeNone !== undefined) {
      filter.includeUngrouped = groups.includeNone;
    }

    return filter;
  }

  public getGroupsCount(): number {
    return metaPillCount({
      includeAll: this.includeAllGroups ?? true,
      includeNone: this.includeUngrouped ?? true,
      items: (this.groups ?? []).filter((g): g is string => !!g),
    });
  }

  public getColumnsCount(): number {
    let count = 0;

    if (this.columns) {
      count += this.columns.length;
    }

    return count;
  }

  public scrubWorkspaceData() {
    this.groups = [];
    this.includeAllGroups = true;
    this.includeUngrouped = true;
    this.columns = this.columns?.filter((x) => !x.startsWith('routine:'));
    this.isCombinedGroupsFilter = false;
  }
}
